import React, { useState, useEffect } from "react";
import { Card, Input, Button, Badge } from "components/ui";
import { toast } from "sonner";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  FunnelIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import { CustomDropdown } from "components/leads/filters";
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import '../../../../../styles/date-range-picker.css'; // custom styles to hide predefined ranges
import dayjs from 'dayjs';
import { Popover, Transition as PopoverTransition } from '@headlessui/react';
import { ChevronDownIcon as ChevronDownIconSmall } from "@heroicons/react/20/solid";
import clsx from "clsx";

// DateRangePickerFilter component using react-date-range with dropdown
function DateRangePickerFilter({ label, value, onChange, onClear, className }) {
  const [dateRange, setDateRange] = useState([
    {
      startDate: value?.fromDate ? new Date(value.fromDate) : new Date(), // Default to today
      endDate: value?.toDate ? new Date(value.toDate) : null,
      key: 'selection'
    }
  ]);

  // Update dateRange when value prop changes
  useEffect(() => {
    setDateRange([
      {
        startDate: value?.fromDate ? new Date(value.fromDate) : new Date(),
        endDate: value?.toDate ? new Date(value.toDate) : null,
        key: 'selection'
      }
    ]);
  }, [value]);

  const handleSelect = (ranges) => {
    const { startDate, endDate } = ranges.selection;
    setDateRange([ranges.selection]);
    
    if (startDate && endDate) {
      onChange({
        fromDate: dayjs(startDate).format('YYYY-MM-DD'),
        toDate: dayjs(endDate).format('YYYY-MM-DD')
      });
    } else if (startDate) {
      // If only start date is selected, don't update yet
      // Wait for both dates to be selected
    }
  };

  const displayValue = () => {
    if (value?.fromDate && value?.toDate) {
      const start = dayjs(value.fromDate).format('MMM D, YYYY');
      const end = dayjs(value.toDate).format('MMM D, YYYY');
      return `${start} - ${end}`;
    } else if (value?.fromDate) {
      return dayjs(value.fromDate).format('MMM D, YYYY');
    }
    return "Select date range";
  };

  return (
    <div className={clsx("w-full", className)}>
      <Popover className="relative">
        {({ open }) => (
          <>
            <Popover.Button className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700 flex items-center justify-between">
              <span className={value?.fromDate && value?.toDate ? "text-gray-900 dark:text-gray-100" : "text-gray-500 dark:text-gray-400"}>
                {displayValue()}
              </span>
              <ChevronDownIconSmall className={`ml-2 h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
            </Popover.Button>
            
            <PopoverTransition
              as="div"            
            >
              <Popover.Panel className="absolute z-50 mt-2 w-auto rounded-lg bg-white shadow-lg ring-1 ring-black/5 dark:bg-gray-800 dark:ring-white/10 left-0 min-w-max max-w-[350px] overflow-hidden">
                <div className="p-2 overflow-x-auto">
                  <DateRangePicker
                    ranges={dateRange}
                    onChange={handleSelect}
                    months={1}
                    direction="horizontal"
                    showSelectionPreview={true}
                    moveRangeOnFirstSelection={false}
                    rangeColors={['#3b82f6']}
                    inputRanges={[]}
                    staticRanges={[]}
                    calendarClassName="custom-calendar"
                  />
                </div>
              </Popover.Panel>
            </PopoverTransition>
          </>
        )}
      </Popover>
    </div>
  );
}

const DocumentsFilter = ({
  filterState = {}, // ✅ default fallback
  onFilterChange = () => {},
  onClearFilters = () => {},
  onSearch = () => {},
}) => {
  // State for accordion expansion
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Local state for search input to prevent excessive re-renders
  const [localSearchValue, setLocalSearchValue] = React.useState(
    filterState?.search ?? ""
  );

  // Parse date range from filter state
  const dateRange = React.useMemo(() => {
    if (filterState?.startDate || filterState?.endDate) {
      return {
        fromDate: filterState.startDate || "",
        toDate: filterState.endDate || ""
      };
    }
    return {};
  }, [filterState?.startDate, filterState?.endDate]);

  const parseDateString = (value) => {
    if (!value) return null;
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, (month || 1) - 1, day || 1);
  };

  const fileType = filterState?.fileType ?? "";
  const startDate = filterState?.startDate ?? "";
  const endDate = filterState?.endDate ?? "";

  // Sync local search with filter state when it changes externally
  React.useEffect(() => {
    setLocalSearchValue(filterState?.search ?? "");
  }, [filterState?.search]);

  // Count active filters (excluding search since it's always visible)
  const activeFilterCount = [fileType, startDate, endDate].filter(
    Boolean,
  ).length;

  // Count all active filters including search for the badge
  const totalActiveCount = [fileType, localSearchValue.trim(), startDate, endDate].filter(
    Boolean,
  ).length;

  // Handle date range change
  const handleDateRangeChange = (dateRangeValue) => {
    // Convert date range format to individual startDate and endDate
    if (dateRangeValue?.fromDate) {
      onFilterChange("startDate", dateRangeValue.fromDate);
    } else {
      onFilterChange("startDate", "");
    }
    
    if (dateRangeValue?.toDate) {
      onFilterChange("endDate", dateRangeValue.toDate);
    } else {
      onFilterChange("endDate", "");
    }
  };

  // Handle date range clear
  const handleDateRangeClear = () => {
    onFilterChange("startDate", "");
    onFilterChange("endDate", "");
  };

  return (
    <Card className="p-3">
      {/* Compact Header - Always visible */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-1">
          {/* Search Bar - Always visible */}
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder="Search documents..."
              value={localSearchValue}
              className={`pl-10 pr-8 text-sm ${
                localSearchValue.trim() ? "ring-primary-200 dark:ring-primary-800 ring-2" : ""
              }`}
              onChange={(e) => {
                const value = e.target.value;
                setLocalSearchValue(value);
                onSearch(value);
              }}
            />
            {localSearchValue && (
              <button
                onClick={() => {
                  setLocalSearchValue("");
                  onSearch("");
                }}
                className="absolute top-1/2 right-2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>

        {/* Filter Toggle Button */}
        <Button
          variant="text"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
        >
          <FunnelIcon className="h-4 w-4" />
          <span className="text-sm font-medium">Filters</span>
          {totalActiveCount > 0 && (
            <Badge
              variant="soft"
              color="primary"
              size="xs"
              className="ml-1"
            >
              {totalActiveCount}
            </Badge>
          )}
          {isExpanded ? (
            <ChevronUpIcon className="h-4 w-4" />
          ) : (
            <ChevronDownIcon className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Expandable Filter Section */}
      {isExpanded && (
        <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {activeFilterCount > 0 
                ? `${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''} applied`
                : 'No filters applied'
              }
            </div>
            {activeFilterCount > 0 && (
              <Button
                variant="text"
                size="xs"
                color="error"
                onClick={onClearFilters}
                className="text-xs"
              >
                <XMarkIcon className="mr-1 h-3 w-3" />
                Clear All
              </Button>
            )}
          </div>

          {/* Filter Controls - Compact Grid */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {/* File Type */}
            <div className="relative">
              <CustomDropdown
                dropdownKey="fileType"
                options={[
                  { value: "", label: "All File Types" },
                  { value: ".pdf", label: "PDF" },
                  { value: ".doc", label: "Word (.doc)" },
                  { value: ".docx", label: "Word (.docx)" },
                  { value: ".jpg", label: "Image (.jpg)" },
                  { value: ".jpeg", label: "Image (.jpeg)" },
                  { value: ".png", label: "Image (.png)" },
                ]}
                selectedValue={fileType}
                onSelect={(value) => onFilterChange("fileType", value)}
                placeholder="File Type"
                className="text-sm"
              />
              {fileType && (
                <div className="bg-primary-500 absolute -top-1 -right-1 h-2 w-2 rounded-full"></div>
              )}
            </div>

            {/* Date Range */}
            <div className="relative sm:col-span-2 lg:col-span-1">             
              <div className="relative">
                <DateRangePickerFilter
                  label=""
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  onClear={handleDateRangeClear}
                  className="w-full"
                />
              </div>
              {(startDate && endDate && parseDateString(startDate) > parseDateString(endDate)) && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                  Start date cannot be after end date
                </p>
              )}
              {dateRange?.fromDate && (
                <div className="bg-primary-500 absolute -top-1 -right-1 h-2 w-2 rounded-full"></div>
              )}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default DocumentsFilter;
